import { TableCellProps } from '@mui/material'
import { ListDensity, TListColumn, TListColumns } from './types'

export const LIST_HEADER_HEIGHT = 48
export const LIST_DEFAULT_ROW_SPACING = 4
export const CHECKBOX_COL_WIDTH = 48

export const LIST_ROW_HEIGHT_BY_DENSITY: Record<ListDensity, number> = {
  compact: 36,
  standard: 48,
  comfortable: 56
}

export const LIST_CELL_PADDING_BY_DENSITY: Record<ListDensity, string> = {
  compact: '0 10px',
  standard: '0 12px',
  comfortable: '0 14px'
}

const buildDefaultColumns = <T>(data: T[]): TListColumn<T>[] => {
  if (data.length === 0) return []
  const firstRow = data[0]
  return Object.keys(firstRow as object).map((key) => ({ field: key as keyof T, label: String(key), flex: 1 }))
}

/**
 * Converts TListColumns<T> Record type to TListColumn<T>[] array for internal use.
 */
export const columnsRecordToArray = <T>(value: T[], columnsRecord?: TListColumns<T>): TListColumn<T>[] => {
  if (!columnsRecord) return buildDefaultColumns(value)
  const keys = Object.keys(columnsRecord) as (keyof T)[]
  return keys.map((key) => {
    const colConfig = columnsRecord[key] ?? {}
    return { field: key, ...colConfig } as TListColumn<T>
  })
}

export function resolveListRowHeight(density?: number | ListDensity): number {
  if (typeof density === 'number' && Number.isFinite(density) && density > 0) return density

  const resolvedDensity: ListDensity = typeof density === 'number' ? ListDensity.standard : (density ?? ListDensity.standard)
  return LIST_ROW_HEIGHT_BY_DENSITY[resolvedDensity]
}

export function resolveListCellPadding(density?: number | ListDensity): string {
  if (typeof density === 'number') return LIST_CELL_PADDING_BY_DENSITY[ListDensity.standard]

  const resolvedDensity: ListDensity = typeof density === 'number' ? ListDensity.standard : (density ?? ListDensity.standard)
  return LIST_CELL_PADDING_BY_DENSITY[resolvedDensity]
}

export function resolveListRowSpacing(spacing?: number): number {
  if (typeof spacing === 'number' && Number.isFinite(spacing) && spacing >= 0) return spacing

  return LIST_DEFAULT_ROW_SPACING
}

/** Default near-end threshold in pixels for triggering near-end events. */
export const LIST_DEFAULT_NEAR_END_THRESHOLD = 80
/** Resolves the final near-end threshold from multiple optional values. */
export function resolveListNearEndThreshold(...threshold: (number | undefined)[]): number {
  const finalThreshold = threshold.filter((value): value is number => typeof value === 'number')
  if (finalThreshold.length === 0) return LIST_DEFAULT_NEAR_END_THRESHOLD
  return Math.max(0, ...finalThreshold)
}

export function resolveListSpacerHeight(count: number, rowHeight: number, rowSpacing: number): number {
  if (count <= 0) return 0
  return count * rowHeight + Math.max(0, count - 1) * rowSpacing
}

/**
 * Resolves pixel widths for all columns using a flex redistribution algorithm.
 * Properly handles min/max constraints by iteratively redistributing overflow
 * from capped columns to uncapped flex siblings — similar to CSS flexbox.
 * Requires the actual container pixel width to compute exact values.
 */
export function resolveFlexColumnWidths<T>(
  columns: TListColumn<T>[],
  selectable: boolean,
  containerWidth: number
): Array<number | string | undefined> {
  const checkboxWidth = selectable ? CHECKBOX_COL_WIDTH : 0
  // Floor to integer to avoid fractional-pixel overflow causing phantom scrollbar
  let pool = Math.floor(containerWidth) - checkboxWidth
  const result: Array<number | string | undefined> = new Array(columns.length).fill(undefined)

  if (containerWidth <= 0) {
    // Container not yet measured: use minWidth as best-guess for each column
    return columns.map((col) => {
      if (col.width !== undefined) return col.width
      return col.minWidth
    })
  }

  // Pass 1: allocate fixed and minWidth-only columns, collect flex columns
  type FlexItem = { i: number; flex: number; min: number; max: number; width: number; frozen: boolean }
  const flexCols: FlexItem[] = []
  for (let i = 0; i < columns.length; i++) {
    const col = columns[i]
    if (col.width !== undefined) {
      result[i] = col.width
      pool -= typeof col.width === 'number' ? col.width : 0
    } else if (!col.flex || col.flex <= 0) {
      const w = col.minWidth ?? 0
      result[i] = w > 0 ? w : undefined
      pool -= w
    } else {
      flexCols.push({ i, flex: col.flex, min: col.minWidth ?? 0, max: col.maxWidth ?? Infinity, width: 0, frozen: false })
    }
  }

  if (flexCols.length === 0 || pool <= 0) return result

  // Pass 2: iterative flex distribution respecting min/max constraints.
  // Each iteration freezes columns that hit a min/max boundary and
  // redistributes the leftover pool among the remaining unfrozen columns.
  for (let iter = 0; iter <= flexCols.length; iter++) {
    const active = flexCols.filter((f) => !f.frozen)
    if (active.length === 0) break
    const totalFlex = active.reduce((s, f) => s + f.flex, 0)
    // Pre-compute all raw widths before mutating pool, so each column is
    // evaluated against the same pool value within the same iteration.
    const raws = active.map((item) => (item.flex / totalFlex) * pool)
    let anyFrozen = false
    for (let j = 0; j < active.length; j++) {
      const item = active[j]
      const raw = raws[j]
      if (raw < item.min) {
        item.width = item.min
        item.frozen = true
        pool -= item.min
        anyFrozen = true
      } else if (raw > item.max) {
        item.width = item.max
        item.frozen = true
        pool -= item.max
        anyFrozen = true
      }
    }
    if (!anyFrozen) {
      // Use floor + assign remainder to last column to guarantee sum === pool
      let distributed = 0
      for (let j = 0; j < active.length; j++) {
        const item = active[j]
        if (j === active.length - 1) {
          item.width = pool - distributed
        } else {
          item.width = Math.floor((item.flex / totalFlex) * pool)
          distributed += item.width
        }
      }
      break
    }
  }

  for (const fc of flexCols) {
    result[fc.i] = fc.width
  }
  return result
}

export const getColumnWidth = <T>(column: TListColumn<T>, totalFixedPx: number, totalFlex: number): number | string | undefined => {
  // Priority 1: Explicit width always wins
  if (column.width !== undefined) return column.width

  // Priority 2: Flex-based proportional width.
  // Subtract all "fixed" columns (explicit width + minWidth-only) so flex columns only share truly remaining space.
  if (column.flex && column.flex > 0 && totalFlex > 0) {
    const flexRatio = column.flex / totalFlex
    const flexCalc = totalFixedPx > 0 ? `calc((100% - ${totalFixedPx}px) * ${flexRatio})` : `${flexRatio * 100}%`
    // When minWidth is also set, use CSS max() so the column never shrinks below minWidth.
    // This is necessary because tableLayout:fixed ignores CSS min-width on cells/cols.
    if (column.minWidth !== undefined) return `max(${column.minWidth}px, ${flexCalc})`

    return flexCalc
  }

  // Priority 3: minWidth-only column — use minWidth as the base layout width.
  // This ensures totalFixedPx correctly reserves space for this column,
  // and the CSS minWidth/maxWidth constraints are still applied via mapSxTableCell.
  if (column.minWidth !== undefined) return column.minWidth

  // Priority 4: Auto-width (no width, flex, or minWidth specified)
  return undefined
}

export const mapSxTableCell = <T>(
  col: TListColumn<T>,
  resolvedWidth: number | string | undefined,
  sx?: TableCellProps['sx']
): TableCellProps['sx'] => {
  return {
    width: resolvedWidth,
    minWidth: col.minWidth,
    maxWidth: col.maxWidth,
    ...sx
  }
}
